import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Package, Truck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'unfulfilled', label: 'To Ship' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'all', label: 'All' },
];

export default function AdminMerchOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('unfulfilled');
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async (which = tab) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/merch/admin/orders?fulfillment=${which}`);
      setOrders(res.data.orders || []);
    } catch (e) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchOrders(tab); }, [tab, fetchOrders]);

  const markShipped = async (orderId) => {
    try {
      await axios.put(`${API}/merch/admin/orders/${orderId}/ship`);
      toast.success('Marked as shipped');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update order');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-merch-orders">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>Merch Orders</h1>
          <p className="text-neutral-500 text-sm">Paid and winner-pack orders with shipping details</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchOrders()} data-testid="refresh-orders-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`orders-tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-600 border border-neutral-200 hover:border-neutral-400'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-neutral-400 py-12 text-center">Loading orders…</p>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-neutral-100">
          <Package className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500" data-testid="no-orders-msg">No orders here yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.order_id} className="bg-white rounded-xl border border-neutral-100 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`order-row-${o.order_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-neutral-900">{o.quantity}× {o.product_name}{o.size ? ` (${o.size})` : ''}</span>
                  {o.payment_status === 'comped' ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">WINNER PACK</span>
                  ) : (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">${(o.amount || 0).toFixed(2)} PAID</span>
                  )}
                  {o.fulfillment_status === 'shipped' ? (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Shipped {o.shipped_at ? new Date(o.shipped_at).toLocaleDateString() : ''}</span>
                  ) : (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">To Ship</span>
                  )}
                </div>
                {o.shipping ? (
                  <p className="text-sm text-neutral-600 truncate">
                    {o.shipping.name} · {o.shipping.email} — {o.shipping.address}, {o.shipping.city}, {o.shipping.province} {o.shipping.postal_code}
                  </p>
                ) : (
                  <p className="text-sm text-neutral-400 italic">No shipping details on file (legacy order)</p>
                )}
                <p className="text-xs text-neutral-400 mt-1">Ordered {new Date(o.created_at).toLocaleString()}</p>
              </div>
              {o.fulfillment_status !== 'shipped' && (
                <Button
                  onClick={() => markShipped(o.order_id)}
                  data-testid={`ship-btn-${o.order_id}`}
                  className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl h-10 px-5 shrink-0"
                >
                  <Truck className="w-4 h-4 mr-2" />Mark as Shipped
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
